/**
 * useAuthorization Hook
 * 
 * React hook for policy-based authorization in UI components.
 * Combines capability checks with policy evaluation to provide
 * a unified authorization decision with explanations.
 * 
 * Usage:
 * 
 * Basic check:
 *   const { allowed, decision, explain } = useAuthorization('items.edit', resource);
 *   if (!allowed) return <DisabledReason decision={decision} />;
 * 
 * With context:
 *   const { allowed } = useAuthorization('items.edit.content', resource, {
 *     submissionWindowOpen: true,
 *     phase: 'shaping'
 *   });
 * 
 * Convenience hooks:
 *   const canEdit = useCanEditItem(itemId, itemData);
 *   const canPublish = useCanPublishList(listId, listData);
 */

import { useMemo, useCallback } from 'react';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  evaluateAuthorization,
  explainDecision,
} from '../../lib/authorization/policyEvaluator';
import { POLICY_RULES } from '../../lib/authorization/policyRules';
import {
  buildResourceContext,
  buildEvaluationContext,
} from '../../lib/authorization/enforce';
import { getCapabilitiesForAction } from '../../types/prioritization/capabilityIds';
import type {
  ResourceContext,
  EvaluationContext,
  PolicyDecision,
  ExplainResult,
  ActorContext,
  CapabilityChecker,
} from '../../lib/authorization/types';
import type { PermissionAction } from '../../types/prioritization/permissions';

// ============================================================================
// Hook Return Type
// ============================================================================

export interface AuthorizationResult {
  /** Whether the action is allowed */
  allowed: boolean;
  
  /** Whether the check is still loading (e.g., waiting for user data) */
  loading: boolean;
  
  /** The full policy decision */
  decision: PolicyDecision | null;
  
  /** User-friendly explanation */
  explain: ExplainResult | null;
  
  /** Force re-evaluation */
  refresh: () => void;
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Check authorization for an action on a resource.
 * 
 * @param action - The action to check (e.g., 'items.edit.content')
 * @param resource - The resource context
 * @param context - Optional evaluation context overrides
 */
export function useAuthorization(
  action: string,
  resource: ResourceContext | null,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const userStore = useUserStore();
  const { userId, roles, currentRole, displayName, resolvedCapabilityIds } = userStore;
  
  // Build actor context - use role-based admin check instead of tier
  const actor: ActorContext = useMemo(() => ({
    userId,
    roleIds: roles || [],
    isAdmin: currentRole === 'system-admin',
    displayName,
  }), [userId, roles, currentRole, displayName]);
  
  // Build capability checker — mirrors buildCapabilityChecker() from enforce.ts so
  // custom DB roles (resolvedCapabilityIds) are recognized alongside built-in role slugs.
  const capabilityChecker: CapabilityChecker = useCallback((permAction: string, scope?) => {
    if (userStore.can(permAction as PermissionAction, scope)) return true;
    const dbCaps = resolvedCapabilityIds ?? [];
    if (dbCaps.length > 0) {
      const required = getCapabilitiesForAction(permAction as PermissionAction);
      if (required.some((cap) => dbCaps.includes(cap))) return true;
    }
    return false;
  }, [userStore, resolvedCapabilityIds]);
  
  // Build evaluation context
  const evalContext = useMemo(() => buildEvaluationContext(context), [context]);
  
  // Evaluate authorization
  const result = useMemo(() => {
    // If no resource, return denied with loading state
    if (!resource) {
      return {
        allowed: false,
        loading: true,
        decision: null,
        explain: null,
      };
    }
    
    // If no user, return denied
    if (!userId) {
      return {
        allowed: false,
        loading: false,
        decision: {
          allowed: false,
          decision: 'deny' as const,
          ruleId: 'no-user',
          reasons: [{ code: 'MISSING_CAPABILITY' }],
          capabilityPassed: false,
        },
        explain: {
          allowed: false,
          summaryKey: 'reason.MISSING_CAPABILITY',
          summary: 'You must be logged in to perform this action.',
        },
      };
    }
    
    const decision = evaluateAuthorization(
      actor,
      { key: action },
      resource,
      evalContext,
      POLICY_RULES,
      capabilityChecker
    );
    
    const explain = explainDecision(decision);
    
    return {
      allowed: decision.allowed,
      loading: false,
      decision,
      explain,
    };
  }, [action, resource, actor, evalContext, capabilityChecker, userId]);
  
  // Refresh callback (forces re-render by depending on new reference)
  const refresh = useCallback(() => {
    // The hook will re-evaluate on next render due to dependency changes
    // This is primarily useful if external state changed
  }, []);
  
  return {
    ...result,
    refresh,
  };
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Check if the current user can edit an item.
 */
export function useCanEditItem(
  itemId: string | null,
  itemData: Record<string, unknown> | null,
  field?: string,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const resource = useMemo(() => {
    if (!itemId || !itemData) return null;
    return buildResourceContext('item', itemId, itemData);
  }, [itemId, itemData]);
  
  const action = field ? `items.edit.${field}` : 'items.edit';
  return useAuthorization(action, resource, context);
}

/**
 * Check if the current user can delete an item.
 */
export function useCanDeleteItem(
  itemId: string | null,
  itemData: Record<string, unknown> | null,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const resource = useMemo(() => {
    if (!itemId || !itemData) return null;
    return buildResourceContext('item', itemId, itemData);
  }, [itemId, itemData]);
  
  return useAuthorization('items.delete', resource, context);
}

/**
 * Check if the current user can shape an item.
 */
export function useCanShapeItem(
  itemId: string | null,
  itemData: Record<string, unknown> | null,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const resource = useMemo(() => {
    if (!itemId || !itemData) return null;
    return buildResourceContext('item', itemId, itemData);
  }, [itemId, itemData]);
  
  return useAuthorization('items.edit', resource, context);
}

/**
 * Check if the current user can publish a list.
 */
export function useCanPublishList(
  listId: string | null,
  listData: Record<string, unknown> | null,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const resource = useMemo(() => {
    if (!listId || !listData) return null;
    return buildResourceContext('list', listId, listData);
  }, [listId, listData]);
  
  return useAuthorization('lists.publish', resource, context);
}

/**
 * Check if the current user can move the cut line on a list.
 */
export function useCanMoveCutLine(
  listId: string | null,
  listData: Record<string, unknown> | null,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const resource = useMemo(() => {
    if (!listId || !listData) return null;
    return buildResourceContext('list', listId, listData);
  }, [listId, listData]);
  
  return useAuthorization('lists.move_cutline', resource, context);
}

/**
 * Check if the current user can manage a session.
 */
export function useCanManageSession(
  sessionId: string | null,
  sessionData: Record<string, unknown> | null,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const resource = useMemo(() => {
    if (!sessionId || !sessionData) return null;
    return buildResourceContext('session', sessionId, sessionData);
  }, [sessionId, sessionData]);
  
  return useAuthorization('session.manage', resource, context);
}

/**
 * Check if the current user can participate in a session.
 */
export function useCanParticipateInSession(
  sessionId: string | null,
  sessionData: Record<string, unknown> | null,
  context?: Partial<EvaluationContext>
): AuthorizationResult {
  const resource = useMemo(() => {
    if (!sessionId || !sessionData) return null;
    return buildResourceContext('session', sessionId, sessionData);
  }, [sessionId, sessionData]);
  
  return useAuthorization('session.participate', resource, context);
}

// ============================================================================
// Batch Authorization
// ============================================================================

/**
 * Check multiple authorizations at once.
 * Useful for determining which actions to show in a menu.
 */
export function useMultipleAuthorizations(
  checks: Array<{
    action: string;
    resource: ResourceContext | null;
    context?: Partial<EvaluationContext>;
  }>
): Array<AuthorizationResult & { action: string }> {
  const userStore = useUserStore();
  const { userId, roles, currentRole, displayName, resolvedCapabilityIds: batchResolvedCaps } = userStore;
  
  // Build actor context - use role-based admin check instead of tier
  const actor: ActorContext = useMemo(() => ({
    userId,
    roleIds: roles || [],
    isAdmin: currentRole === 'system-admin',
    displayName,
  }), [userId, roles, currentRole, displayName]);
  
  const capabilityChecker: CapabilityChecker = useCallback((action: string, scope?) => {
    if (userStore.can(action as PermissionAction, scope)) return true;
    const dbCaps = batchResolvedCaps ?? [];
    if (dbCaps.length > 0) {
      const required = getCapabilitiesForAction(action as PermissionAction);
      if (required.some((cap) => dbCaps.includes(cap))) return true;
    }
    return false;
  }, [userStore, batchResolvedCaps]);
  
  return useMemo(() => {
    return checks.map(({ action, resource, context }) => {
      if (!resource) {
        return {
          action,
          allowed: false,
          loading: true,
          decision: null,
          explain: null,
          refresh: () => {},
        };
      }
      
      const evalContext = buildEvaluationContext(context);
      const decision = evaluateAuthorization(
        actor,
        { key: action },
        resource,
        evalContext,
        POLICY_RULES,
        capabilityChecker
      );
      const explain = explainDecision(decision);
      
      return {
        action,
        allowed: decision.allowed,
        loading: false,
        decision,
        explain,
        refresh: () => {},
      };
    });
  }, [checks, actor, capabilityChecker]);
}

// ============================================================================
// Simple Boolean Hooks
// ============================================================================

/**
 * Simple boolean check - returns just true/false.
 */
export function useIsAllowed(
  action: string,
  resource: ResourceContext | null,
  context?: Partial<EvaluationContext>
): boolean {
  const { allowed } = useAuthorization(action, resource, context);
  return allowed;
}

/**
 * Check if user is admin (has system-admin role).
 */
export function useIsAdmin(): boolean {
  const { currentRole } = useUserStore();
  return currentRole === 'system-admin';
}

/**
 * Check if user is owner of a resource.
 */
export function useIsOwner(resource: ResourceContext | null): boolean {
  const { userId } = useUserStore();
  
  if (!resource || !userId) return false;
  
  return resource.createdBy === userId || resource.ownerId === userId;
}

/**
 * Check if user is the assignee (shaping owner) of a resource.
 */
export function useIsAssignee(resource: ResourceContext | null): boolean {
  const { userId } = useUserStore();

  if (!resource || !userId) return false;

  return resource.assigneeId === userId;
}

/** @deprecated Use useIsAssignee */
export function useIsSteward(resource: ResourceContext | null): boolean {
  return useIsAssignee(resource);
}
